'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import { TheWrapper } from '@hyphengroup/cms-components/components/TheWrapper';
import classNames from 'classnames';
import Button from '../../Button';
import { getTabTarget } from '../helpers/tabTarget';
import { pushDataLayer } from '../pushDataLayer';
import {
  EnumColorSchema,
  ComponentCallToAction,
} from '$/(cms)/_modules/cmsService/types';
import { FeLink } from '$/components/FeLink';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

export const CallToAction: React.FC<ComponentCallToAction> = ({
  titleReq,
  button_text,
  alignment,
  image,
  button_target,
  button_url,
  trackedApplyClick,
  contentText,
  color_scheme,
  productCtaRedirectUrl,
  productName,
  productTitle,
  providerName,
}) => {
  const redirectUrl = (button_url || productCtaRedirectUrl) ?? '';
  const resolvedTarget = getTabTarget(button_target) ?? '_self';

  const handleClick = (title: string) => {
    if (trackedApplyClick) {
      sessionAttributionTracker.recordApplyClick();
      pushDataLayer({
        event: 'product_click_apply',
        component_type: 'card',
        component_title: title,
        action: 'click',
        items: [
          {
            item_id: productTitle,
            item_name: productName,
            item_brand: providerName,
            index: '',
          },
        ],
      });
    } else {
      pushDataLayer({
        event: 'navigate',
        component_type: 'card',
        component_title: title,
        action: 'click',
      });
    }
  };

  const backgroundClasses = {
    [EnumColorSchema.Blue]: 'bg-blue-light',
    [EnumColorSchema.Green]: 'bg-green-light',
    [EnumColorSchema.Orange]: 'bg-orange-light',
    [EnumColorSchema.Red]: 'bg-red-light',
    [EnumColorSchema.White]: 'bg-white-light',
    [EnumColorSchema.Yellow]: 'bg-yellow-light',
    [EnumColorSchema.Transparent]: 'bg-transparent',
    ['default']: 'bg-white-light',
  };
  const resolvedBackgroundClass = backgroundClasses[color_scheme ?? 'default'];

  const imageClasses = classNames(
    'flex items-center justify-center overflow-hidden md:w-auto md:max-w-65 md:shrink-0 md:self-stretch xl:max-w-80',
    resolvedBackgroundClass,
  );

  const wrapperClasses = classNames(
    'flex h-full overflow-hidden rounded-radius/card border border-borders/default',
    {
      'flex-col md:flex-row-reverse': alignment,
      'flex-col md:flex-row': !alignment,
    },
  );

  return (
    <TheWrapper type="simple">
      <div className={wrapperClasses}>
        {image && (
          <div className={imageClasses}>
            <FeLink
              href={redirectUrl}
              target={resolvedTarget}
              className="mx-auto"
              onClick={() => handleClick(`card image_${titleReq}`)}
            >
              <CmsImage
                unoptimized
                src={image.url}
                alt={titleReq}
                height={image.height ?? 9}
                width={image.width ?? 16}
                className="aspect-video size-auto max-h-full max-w-full object-contain"
              />
            </FeLink>
          </div>
        )}
        <div
          className={`w-full px-spacing/padding/x py-spacing/padding/y bg-fill/card ${
            !image ? 'w-full' : ''
          }`}
        >
          <div className="flex flex-col items-stretch gap-spacing/gap/y-sm md:items-start">
            <div className="flex flex-col gap-spacing/gap/y-xs">
              <h3 className="font-semibold text-base leading-[--md] md:text-xl md:leading-[--xl]">
                <FeLink
                  href={redirectUrl}
                  target={resolvedTarget}
                  onClick={() => handleClick(`card title_${titleReq}`)}
                >
                  {titleReq}
                </FeLink>
              </h3>

              <RichText className="font-medium text-gray-600 text-sm leading-line-height/sm md:text-lg md:leading-[--lg]">
                {contentText}
              </RichText>
            </div>

            <div>
              <FeLink href={redirectUrl} target={resolvedTarget}>
                <Button
                  variant="filled"
                  styleType={trackedApplyClick ? 'cta' : 'primary'}
                  className="!w-full !px-4 md:!w-auto"
                  onClick={() => handleClick('CTA_Apply Now')}
                >
                  {button_text}
                </Button>
              </FeLink>
            </div>
          </div>
        </div>
      </div>
    </TheWrapper>
  );
};
